import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { TRY, fmtDate } from "@/lib/format";
import { PageHeader, Empty, StatCard, Segmented, Cell } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RotateCcw, Trash2, FileText, Receipt, Wallet, Trash, Search, CalendarDays, Eraser } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const KINDS = {
  quotes: { type: "quote", label: "Teklifler", icon: FileText, stripe: "bg-primary", tone: "primary" },
  invoices: { type: "invoice", label: "Faturalar", icon: Receipt, stripe: "bg-secondary", tone: "blue" },
  contacts: { type: "contact", label: "Cariler", icon: Wallet, stripe: "bg-[#16A34A]", tone: "success" },
};

export default function TrashBin() {
  const [data, setData] = useState({ quotes: [], invoices: [], contacts: [] });
  const [tab, setTab] = useState("quotes");
  const [q, setQ] = useState("");

  const load = () => api.get("/trash").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const restore = async (type, id) => {
    try { await api.post("/trash/restore", { type, id }); toast.success("Geri getirildi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const purge = async (type, id, title) => {
    if (!(await confirm({ title: "Kalıcı olarak silinsin mi?", description: `${title}\n\nBu kayıt veritabanından tamamen kaldırılır ve geri alınamaz.`, destructive: true, confirmText: "Kalıcı Olarak Sil" }))) return;
    try { await api.delete(`/trash/purge?type=${type}&id=${id}`); toast.success("Kalıcı olarak silindi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [purging, setPurging] = useState(false);
  const purgeAll = async () => {
    const kind = KINDS[tab];
    const n = data[tab].length;
    if (!n) return;
    const extra = tab === "contacts" ? "\n\nCarilerle birlikte gizlenen TÜM bağlı kayıtlar (cari defter, ödemeler, faturalar, teklifler, makbuzlar, irsaliyeler) da kalıcı olarak silinir." : tab === "invoices" ? "\n\nFaturalara bağlı ödeme kayıtları da silinir." : "";
    if (!(await confirm({ title: `${n} ${kind.label.toLocaleLowerCase("tr")} kalıcı olarak silinsin mi?`, description: `Çöp kutusundaki tüm ${kind.label.toLocaleLowerCase("tr")} (${n} kayıt) veritabanından tamamen kaldırılır ve GERİ ALINAMAZ.${extra}`, destructive: true, confirmText: `Evet, ${n} kaydı kalıcı sil` }))) return;
    setPurging(true);
    try {
      const { data: r } = await api.delete(`/trash/purge-all?type=${kind.type}`);
      toast.success(`${r.deleted} ${kind.label.toLocaleLowerCase("tr")}${r.linked ? ` + ${r.linked} bağlı kayıt` : ""} kalıcı olarak silindi`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPurging(false); }
  };

  const total = data.quotes.length + data.invoices.length + data.contacts.length;
  const rows = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    const hit = (...v) => !s || v.some((x) => String(x || "").toLocaleLowerCase("tr").includes(s));
    const list = tab === "quotes"
      ? data.quotes.filter((x) => hit(x.quote_no, x.customer_title)).map((x) => ({ id: x.id, title: x.customer_title || "—", code: x.quote_no || "Teklif", meta: `${(x.items || []).length} kalem`, amount: x.total, deleted_at: x.deleted_at }))
      : tab === "invoices"
        ? data.invoices.filter((x) => hit(x.invoice_no, x.contact_title)).map((x) => ({ id: x.id, title: x.contact_title || "—", code: x.invoice_no || "No yok", meta: `${x.type === "alis" ? "Alış" : "Satış"} · ${fmtDate(x.date)}`, amount: x.total, deleted_at: x.deleted_at }))
        : data.contacts.filter((x) => hit(x.title, x.tax_no)).map((x) => ({ id: x.id, title: x.title, code: x.tax_no || "VKN yok", meta: x.kind === "musteri" ? "Müşteri" : x.kind === "tedarikci" ? "Tedarikçi" : "Cari", amount: null, deleted_at: x.deleted_at }));
    return list.sort((a, b) => String(b.deleted_at || "").localeCompare(String(a.deleted_at || "")));
  }, [data, tab, q]);
  const k = KINDS[tab];
  const Icon = k.icon;

  return (
    <div className="space-y-6 stagger-in" data-testid="trash-page">
      <PageHeader title="Çöp Kutusu" badge={`${total} öğe`} subtitle="Silinen teklif, fatura ve carileri tek tıkla geri getirin ya da kalıcı olarak temizleyin" />

      <div className="grid grid-cols-3 gap-4" data-testid="trash-kpis">
        {Object.entries(KINDS).map(([key, v]) => (
          <StatCard key={key} label={v.label} value={data[key].length} icon={v.icon} tone={v.tone} sub="silinmiş kayıt" testid={`trash-kpi-${key}`} onClick={() => setTab(key)} active={tab === key} />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Segmented value={tab} onChange={setTab} testid="trash-tab" options={Object.entries(KINDS).map(([key, v]) => [key, v.label, data[key].length])} />
        {data[tab].length > 0 && (
          <Button variant="outline" className="h-9 border-destructive/40 text-destructive hover:bg-destructive/5" onClick={purgeAll} disabled={purging} data-testid="trash-purge-all">
            <Eraser className="h-4 w-4 mr-2" />{purging ? "Siliniyor…" : `Tümünü Kalıcı Sil (${data[tab].length})`}
          </Button>
        )}
        <div className="relative flex-1 sm:max-w-xs sm:ml-auto">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ünvan, no veya VKN ara..." className="pl-9 h-9" data-testid="trash-search" />
        </div>
      </div>

      {rows.length === 0 ? (
        total === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground border border-dashed rounded-xl bg-card/50" data-testid="trash-empty">
            <Trash className="h-12 w-12 mb-3 opacity-30" />
            <p className="font-semibold text-foreground/80">Çöp kutusu boş</p>
            <p className="text-sm mt-1">Silinen kayıtlar burada birikir; istediğiniz zaman geri getirebilirsiniz.</p>
          </div>
        ) : <Empty title={`Silinen ${k.label.toLocaleLowerCase("tr")} yok`} desc={q ? "Aramayı değiştirin." : "Bu türde silinmiş kayıt bulunmuyor."} />
      ) : (
        <div className="space-y-2.5" data-testid="trash-list">
          {rows.map((r) => (
            <div key={r.id} className="group relative rounded-xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px" data-testid={`trash-${k.type}-${r.id}`}>
              <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${k.stripe}`} />
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 min-w-0">
                  <Cell label={tab === "contacts" ? "Cari" : "Cari / Müşteri"} className="col-span-2 md:col-span-1">
                    <div className="font-head font-black text-[15px] leading-tight truncate" title={r.title}>{r.title}</div>
                    <div className="text-[11px] text-muted-foreground">{r.meta}</div>
                  </Cell>
                  <Cell label={tab === "contacts" ? "VKN / TCKN" : "Belge No"}><div className="font-mono text-sm font-bold tabular-nums truncate">{r.code}</div></Cell>
                  <Cell label="Silinme Tarihi"><div className="text-sm tabular-nums font-semibold inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{fmtDate(r.deleted_at)}</div></Cell>
                  <Cell label="Tutar"><div className="font-head font-black text-[15px] tabular-nums">{r.amount != null ? TRY(r.amount) : "—"}</div></Cell>
                </div>
                <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
                  <Button size="sm" variant="outline" className="h-8" onClick={() => restore(k.type, r.id)} data-testid={`restore-${k.type}-${r.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1 text-[#16A34A]" />Geri Getir</Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" title="Kalıcı olarak sil" onClick={() => purge(k.type, r.id, `${r.code} · ${r.title}`)} data-testid={`purge-${k.type}-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
